import React from "react";
import { Badge, Group, SimpleGrid, Skeleton, Stack, Text, Tooltip } from "@mantine/core";
import { IconArrowDownRight, IconArrowUpRight } from "@tabler/icons-react";
import { Card, spacePx } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { MonitoringSummary, OperationsSummary } from "@models/adminMonitoring";
import { formatCount, formatMoney } from "@utils/monitoringFormat";

interface TileProps {
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: "warning";
  /** Change against the previous window, when known. */
  delta?: React.ReactNode;
}

const Tile: React.FC<TileProps> = ({ label, value, hint, tone, delta }) => (
  <Card>
    <Stack gap={spacePx[1]}>
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <Text size="sm" c="dimmed">{label}</Text>
        {delta}
      </Group>
      <Text fw={700} fz="h3" lh="xs" c={tone === "warning" ? "orange.8" : undefined}>{value}</Text>
      {hint && <Text size="xs" c="dimmed">{hint}</Text>}
    </Stack>
  </Card>
);

/**
 * Change of a total against the previous window, as a neutral badge: more cost
 * is not "bad" and fewer events is not "good" without context, so no red/green.
 */
const Delta: React.FC<{ current: number; previous: number | undefined }> = ({ current, previous }) => {
  const { t } = useTranslation();
  if (previous === undefined || (previous === 0 && current === 0)) return null;
  const label = previous === 0
    ? t("admin.insights.kpi.deltaNew")
    : `${current >= previous ? "+" : ""}${(((current - previous) / previous) * 100).toFixed(Math.abs(current - previous) / previous < 0.1 ? 1 : 0)}%`;
  return (
    <Tooltip label={t("admin.insights.kpi.deltaHint")}>
      <Badge size="sm" variant="light" color="gray"
             leftSection={current >= previous ? <IconArrowUpRight size={12} /> : <IconArrowDownRight size={12} />}>
        {label}
      </Badge>
    </Tooltip>
  );
};

interface KpiTilesProps {
  summary: MonitoringSummary | null;
  /** The same totals over the previous window; adds a change badge to each tile. */
  previous?: MonitoringSummary | null;
  operations?: OperationsSummary[];
  loading: boolean;
}

/**
 * Headline numbers. Cost is per currency (never summed across currencies) and
 * counts priced events only; unpriced events get their own tile so they never
 * read as free.
 */
const KpiTiles: React.FC<KpiTilesProps> = ({ summary, previous, operations, loading }) => {
  const { t } = useTranslation();
  if (loading && !summary) {
    return (
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} h={104} radius="md" />)}
      </SimpleGrid>
    );
  }
  const [primary, ...others] = summary?.totals ?? [];
  const before = previous ?? undefined;
  const previousCost = primary && before
    ? Number(before.totals.find((x) => x.currency === primary.currency)?.total_cost ?? 0)
    : undefined;
  const extraction = operations?.find((o) => o.source === "extraction");
  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: extraction ? 5 : 4 }}>
      <Tile
        label={t("admin.insights.kpi.cost")}
        value={primary ? formatMoney(primary.total_cost, primary.currency) : formatMoney(0, "USD")}
        delta={primary && <Delta current={Number(primary.total_cost)} previous={previousCost} />}
        hint={others.length ? others.map((o) => formatMoney(o.total_cost, o.currency)).join(" · ") : t("admin.insights.kpi.pricedOnly")}
      />
      <Tile label={t("admin.insights.kpi.events")} value={formatCount(summary?.event_count ?? 0)}
            delta={<Delta current={summary?.event_count ?? 0} previous={before?.event_count} />}
            hint={t("admin.insights.kpi.eventsHint")} />
      <Tile
        label={t("admin.insights.kpi.unpriced")}
        value={formatCount(summary?.unpriced_count ?? 0)}
        tone={summary?.unpriced_count ? "warning" : undefined}
        delta={<Delta current={summary?.unpriced_count ?? 0} previous={before?.unpriced_count} />}
        hint={t("admin.insights.kpi.unpricedHint")}
      />
      <Tile label={t("admin.insights.kpi.users")} value={formatCount(summary?.users_count ?? 0)}
            delta={<Delta current={summary?.users_count ?? 0} previous={before?.users_count} />} />
      {extraction && (
        <Tile
          label={t("admin.insights.kpi.processes")}
          value={formatCount(extraction.total)}
          hint={
            <Group gap="xs" component="span">
              <span>{t("admin.insights.kpi.completed", { count: extraction.completed })}</span>
              <span>{t("admin.insights.kpi.failed", { count: extraction.failed })}</span>
              <span>{t("admin.insights.kpi.pending", { count: extraction.pending })}</span>
            </Group>
          }
        />
      )}
    </SimpleGrid>
  );
};

export default KpiTiles;
